import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid-premium';
import React, { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import DocumentUploadFields from '~/components/documentReview/DocumentUploadFields';
import DocumentUploadProgress from '~/components/documentReview/DocumentUploadProgress';
import { useDocumentUpload } from '~/components/documentReview/useDocumentUpload';
import DragDropZone from '~/components/DragDropZone';
import type { Attachment } from '~/data/schemas/attachment';
import { uploadAttachment } from '~/data/uploadAttachment';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';
import { canAccess, DOCUMENT_REVIEW } from '~/utils/role';

import { UploadDocumentType } from './AddManuallyDropdown';

interface IUploadDocumentDialog {
  open: boolean;
  onClose: () => void;
  onTypeSelected: (type: UploadDocumentType) => void;
  onFilesUploaded: (attachments: Attachment[]) => void;
  /** AI pipeline upload finished; send the user to their review queue. */
  onAiComplete: () => void;
}

type TabKey = 'ai' | 'upload' | 'manual';

interface TabDef {
  key: TabKey;
  label: string;
  caption: string;
  icon: React.ReactNode;
}

const manualTypesFor = (violationLabel: string): { label: string; value: UploadDocumentType }[] => [
  { label: 'Invoice', value: UploadDocumentType.invoice },
  { label: 'Ticket', value: UploadDocumentType.ticket },
  { label: 'Proposal', value: UploadDocumentType.proposal },
  { label: 'Credit', value: UploadDocumentType.credit },
  { label: 'Certificate', value: UploadDocumentType.certificate },
  { label: violationLabel, value: UploadDocumentType.violation },
  { label: 'Inspection Report', value: UploadDocumentType.inspectionReport },
];

/** Pill segmented control matching the mock's tab bar. */
const SegmentedTabs: React.FC<{
  tabs: TabDef[];
  value: TabKey;
  onChange: (key: TabKey) => void;
}> = ({ tabs, value, onChange }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        p: 0.5,
        mb: 2.5,
        // The track wraps the segments, so it needs the segment radius plus its
        // own 4px padding to sit concentrically around them.
        borderRadius: `${SURFACE_RADIUS + 4}px`,
        bgcolor: alpha(theme.palette.text.primary, 0.05),
      }}
    >
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <Button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            disableElevation
            sx={{
              flex: 1,
              px: 1,
              py: 0.75,
              borderRadius: `${SURFACE_RADIUS}px`,
              textTransform: 'none',
              color: active ? 'primary.main' : 'text.secondary',
              bgcolor: active ? 'background.paper' : 'transparent',
              boxShadow: active ? theme.shadows[1] : 'none',
              '&:hover': {
                bgcolor: active ? 'background.paper' : alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {tab.icon}
                <Typography variant="body2" sx={{ fontWeight: active ? 700 : 600 }}>
                  {tab.label}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 400, color: active ? 'primary.main' : 'text.disabled' }}
              >
                {tab.caption}
              </Typography>
            </Box>
          </Button>
        );
      })}
    </Box>
  );
};

const UploadDocumentDialog = ({
  open,
  onClose,
  onTypeSelected,
  onFilesUploaded,
  onAiComplete,
}: IUploadDocumentDialog) => {
  const [, setSearchParams] = useSearchParams();
  const { showSnackbar } = useLAISnackbar();
  const terms = useLocaleTerms();
  const pillSx = BUTTON_SX;
  const {
    userDetails: { role: userRole, isLiftaiAdmin },
  } = useContext(UserContext);
  const manualTypes = useMemo(() => manualTypesFor(terms.violation), [terms.violation]);

  // The AI pipeline (upload → split/classify/extract → review queue) is a
  // consultant/staff capability; the read-only client role never reaches this
  // dialog, but gate the tab explicitly so it can't leak through.
  const showAiPipeline = canAccess(userRole, DOCUMENT_REVIEW) || isLiftaiAdmin;

  const tabs = useMemo<TabDef[]>(() => {
    const list: TabDef[] = [];
    if (showAiPipeline) {
      list.push({
        key: 'ai',
        label: 'AI pipeline',
        caption: 'Recommended',
        icon: <AutoAwesomeIcon fontSize="small" />,
      });
    }
    list.push({
      key: 'upload',
      label: 'Upload & tag',
      caption: 'Manual · with file',
      icon: <UploadFileOutlinedIcon fontSize="small" />,
    });
    list.push({
      key: 'manual',
      label: 'Add manually',
      caption: 'Manual · no file',
      icon: <EditNoteOutlinedIcon fontSize="small" />,
    });
    return list;
  }, [showAiPipeline]);

  const [activeTab, setActiveTab] = useState<TabKey>(showAiPipeline ? 'ai' : 'upload');

  // AI pipeline config + upload, shared with the standalone DocumentUploadDialog.
  const aiUpload = useDocumentUpload({ open: open && showAiPipeline });
  const aiUploading = activeTab === 'ai' && aiUpload.dialogStep === 'uploading';

  // "Upload & tag" holds the file until the user continues, then uploads it and
  // hands off to the manual tagging flow (matching the mock's two-step feel).
  const [tagFiles, setTagFiles] = useState<File[]>([]);
  const [tagUploading, setTagUploading] = useState(false);

  const handleClose = () => {
    aiUpload.resetState();
    setTagFiles([]);
    setTagUploading(false);
    onClose();
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('propertyId');
      newParams.delete('carId');
      return newParams;
    });
  };

  const handleContinueToTagging = async () => {
    if (tagFiles.length === 0) return;
    setTagUploading(true);
    try {
      const attachments = await Promise.all(
        tagFiles.map((file) => uploadAttachment({ file, name: file.name, fileType: 'pdf' })),
      );
      onFilesUploaded(attachments);
    } catch (err) {
      // Fire-and-forget onClick: surface the failure instead of leaving the
      // button to silently re-enable with an unhandled promise rejection.
      const message = err instanceof Error ? err.message : 'Failed to upload file';
      showSnackbar(message, { variant: 'error' });
    } finally {
      setTagUploading(false);
    }
  };

  const footerHint = (() => {
    if (activeTab === 'ai') {
      if (aiUpload.uploadComplete) return '✓ Uploaded — processing your documents…';
      if (!aiUpload.portfolio) return 'Select a portfolio to begin.';
      return aiUpload.fileEntries.length > 0
        ? `Ready to process ${aiUpload.fileEntries.length} file${aiUpload.fileEntries.length !== 1 ? 's' : ''}.`
        : 'Drop a PDF to begin.';
    }
    if (activeTab === 'upload') {
      return tagFiles.length > 0
        ? `Ready to tag “${tagFiles[0].name}”.`
        : 'Upload a PDF to tag its documents yourself.';
    }
    return 'Pick a document type to enter it by hand.';
  })();

  return (
    <Dialog
      open={open}
      // Ignore backdrop/Escape while the AI upload is in flight so a stray click
      // can't drop the dialog mid-upload; the Cancel button stays available.
      onClose={aiUploading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle component="div">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h2">
            Add to Property
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <GridCloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {aiUploading ? (
          <DocumentUploadProgress upload={aiUpload} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add invoices, tickets, proposals, certificates, {terms.violations.toLowerCase()} and
              inspection reports — choose how below.
            </Typography>

            {tabs.length > 1 ? (
              <SegmentedTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
            ) : null}

            {activeTab === 'ai' ? (
              <>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    mb: 2.5,
                    pl: 2.5,
                    color: 'text.secondary',
                    '& li': { mb: 0.75 },
                  }}
                >
                  <Typography component="li" variant="body2">
                    <strong>Upload one or more PDFs.</strong> Drop a whole batch at once — each file
                    is processed on its own.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Multi-document PDFs are split automatically</strong> — you confirm where
                    each one starts and ends. If a file is a single document, tick{' '}
                    <em>Keep as one document</em> to skip splitting.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>We classify and extract every document</strong> — type, dates, amounts,
                    vendor and the rest — automatically.
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>You review and approve each one.</strong> Check or fix the extracted
                    fields; approving creates the record (invoice, ticket, contract, and so on).
                  </Typography>
                  <Typography component="li" variant="body2">
                    <strong>Auto-file (optional, off by default):</strong> confident invoices and
                    tickets are created for you automatically, skipping that last approval;
                    everything else still waits for your review.
                  </Typography>
                </Box>
                <DocumentUploadFields upload={aiUpload} variant="simplified" />
              </>
            ) : null}

            {activeTab === 'upload' ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Already have the file? Upload it and tag each document yourself — no AI
                  processing.
                </Typography>
                <DragDropZone
                  acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
                  acceptedFileTypesLabel="PDF"
                  fileSizeLimit={5}
                  onFileSelected={setTagFiles}
                  loading={tagUploading}
                />
                {tagFiles.length > 0 ? (
                  <Typography variant="body2" sx={{ mt: 1.5 }}>
                    Selected: {tagFiles[0].name}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            {activeTab === 'manual' ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No file to hand? Pick a document type and enter the details by hand.
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                    gap: 1.25,
                  }}
                >
                  {manualTypes.map((type) => (
                    <Button
                      key={type.value}
                      variant="outlined"
                      onClick={() => onTypeSelected(type.value)}
                      sx={{
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        // The panel radius, not the pill: these are choice tiles in a grid, sitting under a tab
                        // bar cut to the same corner.
                        borderRadius: `${SURFACE_RADIUS}px`,
                      }}
                    >
                      {type.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {footerHint}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button onClick={handleClose} sx={pillSx}>
            Cancel
          </Button>
          {activeTab === 'ai' && aiUpload.uploadComplete ? (
            <Button variant="contained" onClick={onAiComplete} sx={pillSx}>
              View uploads
            </Button>
          ) : null}
          {activeTab === 'ai' && !aiUploading ? (
            <Button
              variant="contained"
              onClick={aiUpload.handleUpload}
              disabled={aiUpload.fileEntries.length === 0 || !aiUpload.portfolio}
              sx={pillSx}
            >
              Start extraction
            </Button>
          ) : null}
          {activeTab === 'upload' ? (
            <Button
              variant="contained"
              onClick={handleContinueToTagging}
              disabled={tagFiles.length === 0 || tagUploading}
              sx={pillSx}
            >
              Continue to tagging
            </Button>
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentDialog;
